import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/storage';
import SuperAdminLayout from '../components/SuperAdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Upload, Trash2, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import confirm from '../utils/confirm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminLanguages = () => {
  const { t } = useTranslation();
  const [languages, setLanguages] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({
    language_code: '',
    language_name: '',
    file: null
  });

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    try {
      const token = getToken();
      console.log('Fetching languages with token:', token ? 'present' : 'missing');
      
      if (!token) {
        console.error('No authentication token found');
        toast.error('Authentication error. Please log in again.');
        return;
      }
      
      const response = await axios.get(`${API}/admin/languages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Languages loaded:', response.data.length);
      setLanguages(response.data);
    } catch (error) {
      console.error('Error loading languages:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please log in again.');
      } else {
        toast.error(t('superAdmin.languages.errorLoadingLanguages'));
      }
    }
  };

  const handleDownload = async (languageCode) => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin/languages/${languageCode}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${languageCode}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(t('superAdmin.languages.languageDownloaded'));
    } catch (error) {
      toast.error(t('superAdmin.languages.errorDownloading'));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      toast.error(t('superAdmin.languages.selectFile'));
      return;
    }

    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('language_code', uploadData.language_code);
      formData.append('language_name', uploadData.language_name);

      await axios.post(`${API}/admin/languages/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(t('superAdmin.languages.languageUploaded'));
      setShowUploadModal(false);
      fetchLanguages();
      setUploadData({ language_code: '', language_name: '', file: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('superAdmin.languages.errorUploading'));
    }
  };

  const handleDelete = async (languageCode) => {
    console.log('Delete clicked for:', languageCode);
    
    if (!await confirm.danger(t('superAdmin.languages.confirmDelete'))) {
      return;
    }

    try {
      const token = getToken();
      console.log('Deleting language:', languageCode);
      await axios.delete(`${API}/admin/languages/${languageCode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('superAdmin.languages.languageDeleted'));
      fetchLanguages();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || t('superAdmin.languages.errorDeleting'));
    }
  };

  const handleToggleVisibility = async (languageCode) => {
    console.log('Toggle visibility clicked for:', languageCode);
    
    // Optimistically update UI
    setLanguages(prevLanguages => 
      prevLanguages.map(lang => 
        lang.language_code === languageCode 
          ? { ...lang, is_visible: !lang.is_visible }
          : lang
      )
    );
    
    try {
      const token = getToken();
      console.log('Toggling visibility for:', languageCode);
      await axios.put(`${API}/admin/languages/${languageCode}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('superAdmin.languages.visibilityUpdated'));
      // Refresh to get actual state from server
      fetchLanguages();
    } catch (error) {
      console.error('Toggle visibility error:', error);
      toast.error(t('superAdmin.languages.errorTogglingVisibility'));
      // Revert optimistic update on error
      fetchLanguages();
    }
  };

  return (
    <SuperAdminLayout>
      <div className="admin-header">
        <h1>{t('superAdmin.languages.title')}</h1>
        <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
          <Upload size={20} /> {t('superAdmin.languages.addLanguage')}
        </button>
      </div>

      <div className="admin-table">
        <div className="table-header">
          <h2>{t('superAdmin.languages.availableLanguages')}</h2>
        </div>
        <table>
          <thead>
            <tr>
              <th>{t('superAdmin.languages.languageName')}</th>
              <th>{t('superAdmin.languages.languageCode')}</th>
              <th>{t('common.status')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {languages.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                  {t('superAdmin.languages.noLanguagesYet')}
                </td>
              </tr>
            ) : (
              languages.map(lang => (
                <tr key={lang.language_code}>
                  <td>{lang.language_name}</td>
                  <td><code style={{ background: 'rgba(80, 227, 194, 0.1)', padding: '0.25rem 0.5rem', borderRadius: '4px', color: '#50E3C2' }}>{lang.language_code}</code></td>
                  <td>
                    <span className={lang.is_visible ? 'badge badge-success' : 'badge badge-danger'}>
                      {lang.is_visible ? t('superAdmin.languages.visible') : t('superAdmin.languages.hidden')}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleToggleVisibility(lang.language_code);
                        }}
                        title={lang.is_visible ? t('superAdmin.languages.hide') : t('superAdmin.languages.show')}
                        style={{ padding: '0.5rem', cursor: 'pointer' }}
                      >
                        {lang.is_visible ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDownload(lang.language_code);
                        }}
                        title={t('superAdmin.languages.download')}
                        style={{ padding: '0.5rem', cursor: 'pointer' }}
                      >
                        <Download size={16} />
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!['no', 'en'].includes(lang.language_code)) {
                            await handleDelete(lang.language_code);
                          } else {
                            toast.error(t('superAdmin.languages.cannotDeleteDefault'));
                          }
                        }}
                        title={t('superAdmin.languages.delete')}
                        disabled={['no', 'en'].includes(lang.language_code)}
                        style={{ 
                          padding: '0.5rem', 
                          cursor: ['no', 'en'].includes(lang.language_code) ? 'not-allowed' : 'pointer',
                          opacity: ['no', 'en'].includes(lang.language_code) ? 0.5 : 1
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '3rem', maxWidth: '800px' }}>
        <div className="admin-table" style={{ padding: '2rem' }}>
          <h2 style={{ color: 'white', marginBottom: '1rem' }}>{t('superAdmin.languages.howToUse')}</h2>
          <ol style={{ color: '#e0e0e0', lineHeight: 1.8 }}>
            <li>{t('superAdmin.languages.step1')}</li>
            <li>{t('superAdmin.languages.step2')}</li>
            <li>{t('superAdmin.languages.step3')}</li>
            <li>{t('superAdmin.languages.step4')}</li>
          </ol>
          <p style={{ color: '#a0a0a0', marginTop: '1rem', fontSize: '0.9rem' }}>
            {t('superAdmin.languages.note')}
          </p>
        </div>
      </div>

      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('superAdmin.languages.addLanguage')}</h2>
            <form onSubmit={handleUpload}>
              <div className="form-group">
                <label>{t('superAdmin.languages.languageCode')}</label>
                <input
                  type="text"
                  value={uploadData.language_code}
                  onChange={(e) => setUploadData({...uploadData, language_code: e.target.value})}
                  placeholder="sv, da, de, etc."
                  required
                  maxLength={5}
                />
                <small style={{ color: '#9ca3af', display: 'block', marginTop: '0.25rem' }}>
                  {t('superAdmin.languages.languageCodeHint')}
                </small>
              </div>

              <div className="form-group">
                <label>{t('superAdmin.languages.languageName')}</label>
                <input
                  type="text"
                  value={uploadData.language_name}
                  onChange={(e) => setUploadData({...uploadData, language_name: e.target.value})}
                  placeholder={t('superAdmin.languages.languageNamePlaceholder')}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('superAdmin.languages.jsonFile')}</label>
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => setUploadData({...uploadData, file: e.target.files[0]})}
                  required
                />
                <small style={{ color: '#9ca3af', display: 'block', marginTop: '0.25rem' }}>
                  {t('superAdmin.languages.jsonFileHint')}
                </small>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button type="submit" className="btn btn-primary">{t('superAdmin.languages.upload')}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowUploadModal(false)}>{t('common.cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminLanguages;
